import { AnimatePresence, motion } from "framer-motion";
import { Calendar, Clock, Plus, Search, X } from "lucide-react";
import React, { useCallback, useRef, useState } from "react";
import { useSessionManager } from "../hooks/useSessionManager";
import { sessionTypeOptions } from "./sessionManagerHelpers";
import Button from "./ui/Button";
import Input from "./ui/Input";
import Select from "./ui/Select";
import Textarea from "./ui/Textarea";
import BaseModal from "./ui/BaseModal";

const SessionManager = ({
  selectedPatient,
  fetchPatientDetails,
  onSelectSession,
  onDeleteSession,
  selectedSessionForAnalysis,
}) => {
  const scrollContainerRef = useRef(null);
  const [sortType, setSortType] = useState("asc");
  const [showModal, setShowModal] = useState(false);
  const [sessionIdToDelete, setSessionIdToDelete] = useState(null);

  const {
    newSession,
    isLoading,
    error,
    showForm,
    setShowForm,
    searchQuery,
    handleSearchChange,
    displayedSessions,
    hasMore,
    loadMore,
    handleInputChange,
    handleSubmit,
  } = useSessionManager(
    selectedPatient,
    fetchPatientDetails,
    selectedPatient?.sessions || [],
    sortType
  );

  // Set up intersection observer for infinite scroll
  const observer = useRef();
  const lastSessionElementRef = useCallback(
    (node) => {
      if (isLoading) return;
      if (observer.current) observer.current.disconnect();
      observer.current = new IntersectionObserver((entries) => {
        if (entries[0].isIntersecting && hasMore) {
          loadMore();
        }
      });
      if (node) observer.current.observe(node);
    },
    [isLoading, hasMore, loadMore]
  );

  if (!selectedPatient) {
    return (
      <motion.div
        className="p-4 md:p-8 bg-gray-50 rounded-xl border border-dashed border-gray-300 text-center"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.3 }}
      >
        <Calendar
          className="h-12 w-12 md:h-16 md:w-16 mx-auto text-gray-400 mb-3 md:mb-4"
          strokeWidth={1.5}
        />
        <p className="text-gray-600 text-sm md:text-base">
          Select a patient to manage sessions
        </p>
      </motion.div>
    );
  }

  return (
    <div className="flex flex-col h-full">
      <div className="flex items-center left group flex-wrap gap-2">
        <Button
          onClick={() => setShowForm(!showForm)}
          variant="primary"
          className={`px-3 py-1 md:px-4 md:py-1.5 text-white text-xs md:text-sm font-medium rounded-lg shadow-sm transition-all ${
            showForm
              ? "bg-gray-600 hover:bg-gray-700"
              : "bg-gradient-to-r from-[#2a5caa] to-[#3963aa] hover:shadow-md"
          }`}
        >
          {showForm ? <X className="w-4 h-4" /> : <Plus className="w-4 h-4" />}
        </Button>
        <h3 className="text-base md:text-lg font-semibold text-[#1c3f70]">
          Sessions for {selectedPatient.name}
        </h3>
        <Select
          name="sort"
          defaultValue={sortType}
          options={[
            { value: "asc", label: "Latest" },
            { value: "desc", label: "Oldest" },
          ]}
          onChange={(e) => setSortType(e.target.value)}
          width="ml-auto"
        />
      </div>

      <AnimatePresence>
        {showForm && (
          <motion.div
            className="bg-white rounded-xl p-3 md:p-5 border border-gray-100 shadow-md mt-4"
            initial={{ opacity: 0, height: 0, y: -10 }}
            animate={{ opacity: 1, height: "auto", y: 0 }}
            exit={{ opacity: 0, height: 0, y: -10 }}
            transition={{ duration: 0.3 }}
          >
            <form onSubmit={handleSubmit} className="space-y-3 md:space-y-4">
              <Select
                label="Session Type"
                name="session_type"
                defaultValue={newSession.session_type}
                options={sessionTypeOptions}
                onChange={handleInputChange}
                width="w-full"
                className="mt-3"
              />

              <Textarea
                label="Session Notes"
                name="notes"
                value={newSession.notes}
                onChange={handleInputChange}
                rows={2}
                className="text-sm"
              />

              <div className="flex space-x-2 pt-1 md:pt-2">
                <Button
                  type="submit"
                  disabled={isLoading}
                  className={`px-3 py-1.5 md:px-5 md:py-2 bg-gradient-to-r from-[#2a5caa] to-[#3963aa] text-white rounded-lg text-xs md:text-sm font-medium shadow-sm hover:shadow-md transition-all ${
                    isLoading ? "opacity-70 cursor-not-allowed" : ""
                  }`}
                >
                  {isLoading ? "Creating..." : "Create Session"}
                </Button>
              </div>
            </form>
            {error && (
              <motion.div
                className="mt-3 p-2 md:p-3 bg-red-50 border border-red-200 rounded-lg"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
              >
                <p className="text-red-600 text-xs md:text-sm">{error}</p>
              </motion.div>
            )}
          </motion.div>
        )}
      </AnimatePresence>

      {/* Search input */}
      <div className="relative mt-4">
        <Input
          type="text"
          placeholder="Search sessions..."
          value={searchQuery}
          onChange={handleSearchChange}
          className="pl-10 text-sm w-full"
        />
        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
          <Search className="h-5 w-5 text-gray-400" />
        </div>
      </div>

      {/* Sessions List with Scrolling */}
      <div
        className="mt-4 flex flex-col h-full"
        ref={scrollContainerRef}
        style={{ minHeight: "300px" }}
      >
        {displayedSessions.length === 0 ? (
          <motion.div
            className="text-center p-4 md:p-8 bg-gray-50 rounded-xl border border-dashed border-gray-300 h-full flex flex-col justify-center"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
          >
            {selectedPatient?.sessions?.length === 0 ? (
              <>
                <Clock
                  className="h-12 w-12 md:h-16 md:w-16 mx-auto text-gray-400 mb-3 md:mb-4"
                  strokeWidth={1.5}
                />
                <p className="text-gray-600 text-sm md:text-base">
                  No sessions found for this patient. Create one using the
                  button above.
                </p>
              </>
            ) : (
              <>
                <Search
                  className="h-12 w-12 md:h-16 md:w-16 mx-auto text-gray-400 mb-3 md:mb-4"
                  strokeWidth={1.5}
                />
                <p className="text-gray-600 text-sm md:text-base">
                  No sessions match your search. Try different keywords.
                </p>
              </>
            )}
          </motion.div>
        ) : (
          <div className="overflow-y-auto overflow-x-hidden h-full pr-2">
            <motion.ul className="space-y-3 mb-2">
              {displayedSessions.map((session, index) => {
                const isLastElement = index === displayedSessions.length - 1;
                return (
                  <motion.li
                    key={session.id}
                    ref={isLastElement ? lastSessionElementRef : null}
                    className={`p-3 md:p-4 rounded-xl border transition-all duration-300 cursor-pointer group ${
                      selectedSessionForAnalysis?.id === session.id
                        ? "bg-gradient-to-r from-green-50 to-green-100 border-green-200 shadow"
                        : "bg-white border-gray-100 hover:shadow-md"
                    }`}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.05, duration: 0.3 }}
                    onClick={() => onSelectSession(session)}
                  >
                    <div className="flex justify-between items-start flex-col space-y-2">
                      <div className="w-full">
                        <div className="flex flex-wrap items-center gap-2 mb-1.5">
                          <span
                            className={`px-2 py-1 text-xs rounded-full font-medium ${
                              session.session_type === "Diagnostic"
                                ? "bg-blue-100 text-blue-700"
                                : session.session_type === "Follow-up"
                                  ? "bg-purple-100 text-purple-700"
                                  : session.session_type === "Treatment"
                                    ? "bg-orange-100 text-orange-700"
                                    : "bg-gray-100 text-gray-700"
                            }`}
                          >
                            {session.session_type}
                          </span>
                          {selectedSessionForAnalysis?.id === session.id && (
                            <span className="bg-green-100 text-green-700 px-2 py-1 text-xs rounded-full font-medium">
                              Selected
                            </span>
                          )}
                        </div>
                        <div className="text-sm text-gray-500 mb-1">
                          {new Date(session.created_at).toLocaleString()}
                        </div>
                        <p className="p-2 bg-gray-50 text-sm text-gray-700 rounded-lg">
                          <span className="line-clamp-3">
                            {session.notes || "No notes provided"}
                          </span>
                        </p>
                        <div className="text-xs text-gray-400 mt-1 truncate">
                          ID: {session.id}
                        </div>
                      </div>
                      <div className="w-full flex justify-end gap-2">
                        <Button
                          variant="danger"
                          onClick={(e) => {
                            e.stopPropagation();
                            setSessionIdToDelete(session.id);
                            setShowModal(true);
                          }}
                          className={
                            "px-3 py-1 text-xs font-medium rounded text-white invisible opacity-0 group-hover:visible group-hover:opacity-100 transition-opacity"
                          }
                        >
                          Delete
                        </Button>
                        {selectedSessionForAnalysis?.id !== session.id && (
                          <Button
                            onClick={(e) => {
                              e.stopPropagation();
                              onSelectSession(session);
                            }}
                            className={`px-3 py-1 text-xs font-medium rounded transition-all text-white ${
                              !(selectedSessionForAnalysis?.id !== session.id)
                                ? "invisible"
                                : ""
                            } ${
                              selectedSessionForAnalysis?.id === session.id
                                ? "bg-green-600  hover:bg-green-700"
                                : "bg-blue-100 text-white bg-blue-200"
                            }`}
                          >
                            Select
                          </Button>
                        )}
                      </div>
                    </div>
                  </motion.li>
                );
              })}
              {isLoading && (
                <motion.div
                  className="flex justify-center p-4"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                >
                  <div className="w-5 h-5 rounded-full bg-[#2a5caa] animate-pulse"></div>
                </motion.div>
              )}
            </motion.ul>
          </div>
        )}
      </div>
      <BaseModal
        open={showModal}
        title="Confirm Delete Session"
        description="Are you sure you want to delete this session? This action cannot be undone."
        onClose={() => setShowModal(false)}
        onConfirm={() => {
          onDeleteSession(sessionIdToDelete, selectedPatient.id);
          setShowModal(false);
          onSelectSession(null);
        }}
      >
        ID: {sessionIdToDelete}
      </BaseModal>
    </div>
  );
};

export default SessionManager;
